import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { StorageKeys } from '@/lib/localStorage';
import { THEME_TO_ATTR, nextTheme, nextFontScale, BG_PATTERN_DEFAULT, FONT_SCALE_DEFAULT } from '@/types';
import type { BgPattern, Density, Decoration, FontScale, ThemeName, UiPrefs } from '@/types';
import { defaultUiPrefs } from './defaults';
import { api } from '@/api';
import { useAuthContextOptional } from './AuthContext';

interface ThemeContextValue {
  theme: ThemeName;
  density: Density;
  decoration: Decoration;
  bg: BgPattern;
  fontScale: FontScale;
  /** Аватар-бадди профиля (Profile v2 · 9 JUL 2026) · undefined = дневная ротация. */
  avatarBuddy: string | undefined;
  setAvatarBuddy: (b: string | undefined) => void;
  setTheme: (t: ThemeName) => void;
  setDensity: (d: Density) => void;
  setDecoration: (d: Decoration) => void;
  setBg: (b: BgPattern) => void;
  cycleTheme: () => void;
  setFontScale: (f: FontScale) => void;
  cycleFontScale: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// local-dirty-wins sync flag (9 JUL 2026 · «тема сама меняется после рефреша»).
// Set while a local UiPrefs change has NOT been confirmed to the server (e.g. a
// PATCH failed because the edge fn был briefly 503). While set, the initial GET
// must NOT overwrite the local pick — otherwise the just-picked theme reverts to
// the stale server value on the next reload. Cleared on a successful PATCH.
const UI_UNSYNCED_KEY = 'qastudio.v1.ui.unsynced';
const markUiUnsynced = (): void => {
  try {
    window.localStorage.setItem(UI_UNSYNCED_KEY, '1');
  } catch {
    /* storage blocked */
  }
};
const clearUiUnsynced = (): void => {
  try {
    window.localStorage.removeItem(UI_UNSYNCED_KEY);
  } catch {
    /* ignore */
  }
};
const isUiUnsynced = (): boolean => {
  try {
    return window.localStorage.getItem(UI_UNSYNCED_KEY) === '1';
  } catch {
    return false;
  }
};

/** PATCH UiPrefs to the server with a small retry; clears the unsynced flag on
 *  success so the next mount trusts the server again. On total failure it keeps
 *  the flag set → local wins and re-pushes on the next mount. */
async function patchUiPrefsWithRetry(
  payload: Pick<UiPrefs, 'theme' | 'density' | 'decoration' | 'fontScale' | 'avatarBuddy'>,
  isCancelled: () => boolean,
): Promise<void> {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (isCancelled()) return;
    try {
      await api.uiPrefs.patch(payload);
      if (!isCancelled()) clearUiUnsynced();
      return;
    } catch (err) {
      if (isCancelled()) return;
      if (attempt === 2) {
        console.warn('[theme-sync] patch failed after retries · local kept · re-push next mount', err);
        return;
      }
      await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
    }
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useLocalStorage<UiPrefs>(StorageKeys.ui, defaultUiPrefs());
  // Latest prefs for the run-once fetch effect's dirty re-push (avoids stale closure).
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;

  // Riso [data-bg] system · per slot_riso_final_ds A1.2 (15 MAY 2026).
  // Frontend-local state (NOT in UiPrefs shared schema · avoids backend coordination
  // per ADR-002). Default 'paper' matches index.html static attribute. Future
  // Tweaks panel slot can expose setBg UI control + optionally persist.
  const [bg, setBg] = useState<BgPattern>(BG_PATTERN_DEFAULT);

  // Sync to <html> attributes whenever prefs change.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    html.dataset.theme = THEME_TO_ATTR[prefs.theme];
    html.dataset.density = prefs.density;
    html.dataset.decoration = prefs.decoration;
    // MOBILE_READABILITY (2026-06-06): text-size A/A+/A++ -> --font-scale CSS var.
    html.dataset.fontScale = prefs.fontScale ?? FONT_SCALE_DEFAULT;
    // Add transition class only after first paint to avoid initial flash.
    requestAnimationFrame(() => {
      html.classList.add('theme-transition');
    });
  }, [prefs.theme, prefs.density, prefs.decoration, prefs.fontScale]);

  // Separate sync for bg — non-persisted local state (different lifecycle from prefs).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.bg = bg;
  }, [bg]);

  // One-shot OS auto-detect · slot_dark_theme DT.4 (2026-05-18 · Genie pick Y).
  //
  // Runs once on first mount. If the user has never written к localStorage
  // qastudio.v1.ui (== first visit или fresh browser profile), we respect
  // their OS prefers-color-scheme и seed prefs.theme = 'deepstudio' when
  // the OS is in dark mode. After this first write, useLocalStorage
  // persists the choice · subsequent mounts skip this branch because
  // localStorage[StorageKeys.ui] !== null.
  //
  // Once the user has explicitly toggled (or any UiPrefs setter has fired),
  // their pick wins permanently · we never override based on OS changes after
  // first-mount. Cross-device sync (ADR-002 + ADR-007) handles the
  // multi-device case через UiPrefs shared schema, so this auto-detect only
  // fires per-device-per-fresh-profile · expected behavior.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(StorageKeys.ui);
    if (stored !== null) return; // user already has a stored preference · respect it
    const mq = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
    if (mq?.matches) {
      setPrefs((prev) => ({ ...prev, theme: 'deepstudio' }));
    }
    // ELSE: warmsunset default applies · no setPrefs call (avoids tripping
    // localStorage write for users who match the default · keeps "has the
    // user interacted yet" signal accurate for downstream features).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FS slot · cross-device theme/density/decoration sync via /ui-prefs edge
  // function. Mirrors the ProgressContext server-as-canonical pattern (per
  // ADR-006/007) but lighter — UiPrefs has no LWW, server is always wins on
  // initial fetch. Without this, theme picked on desktop wouldn't follow
  // to mobile / other device on next sign-in.
  // Optional auth context · ThemeProvider renders without AuthProvider in
  // many unit test fixtures, so we defensively bail when no auth context is
  // present. Sync simply doesn't fire — local state still works.
  const auth = useAuthContextOptional();
  const isAuthed = auth?.isAuthed ?? false;
  const hasFetchedOnce = useRef(false);
  const suppressInitialPush = useRef(true);

  useEffect(() => {
    if (!isAuthed) {
      hasFetchedOnce.current = false;
      suppressInitialPush.current = true;
      return;
    }
    if (hasFetchedOnce.current) return;
    hasFetchedOnce.current = true;

    let cancelled = false;
    void (async () => {
      try {
        // local-dirty-wins: a local pref change that never confirmed to the server
        // (PATCH failed while the edge был down) must NOT be reverted by the stale
        // server value on reload. Keep the local pick, re-push it, and skip the GET.
        if (isUiUnsynced()) {
          await patchUiPrefsWithRetry(
            {
              theme: prefsRef.current.theme,
              density: prefsRef.current.density,
              decoration: prefsRef.current.decoration,
              fontScale: prefsRef.current.fontScale,
              avatarBuddy: prefsRef.current.avatarBuddy,
            },
            () => cancelled,
          );
          return;
        }
        const server = await api.uiPrefs.get();
        if (cancelled) return;
        setPrefs((prev) =>
          prev.theme === server.theme &&
          prev.density === server.density &&
          prev.decoration === server.decoration &&
          (server.fontScale ?? prev.fontScale) === prev.fontScale &&
          (server.avatarBuddy ?? prev.avatarBuddy) === prev.avatarBuddy
            ? prev
            : {
                ...prev,
                theme: server.theme,
                density: server.density,
                decoration: server.decoration,
                // Old deployed edge fn may not echo fontScale yet (CF-MR-EDGE-REDEPLOY) ·
                // keep the local value in that case instead of dropping to default.
                fontScale: server.fontScale ?? prev.fontScale,
                avatarBuddy: server.avatarBuddy ?? prev.avatarBuddy,
              },
        );
      } catch (err) {
        console.warn('[theme-sync] fetch failed', err);
      } finally {
        // Allow subsequent setter-driven changes к propagate as PATCH /ui-prefs.
        suppressInitialPush.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthed, setPrefs]);

  // Push local pref changes к server (PATCH /ui-prefs) while authed.
  // suppressInitialPush guards against a feedback loop right after initialFetch
  // hydrates state from server — that change must not trigger a PATCH back.
  useEffect(() => {
    if (!isAuthed) return;
    if (suppressInitialPush.current) return;
    // Mark unsynced BEFORE the request so a reload mid-flight (or on failure)
    // keeps the local pick instead of letting the stale server value win.
    markUiUnsynced();
    let cancelled = false;
    void patchUiPrefsWithRetry(
      {
        theme: prefs.theme,
        density: prefs.density,
        decoration: prefs.decoration,
        // Stripped (not errored) by the pre-MR deployed edge fn · harmless.
        fontScale: prefs.fontScale,
        avatarBuddy: prefs.avatarBuddy,
      },
      () => cancelled,
    );
    return () => {
      cancelled = true;
    };
  }, [isAuthed, prefs.theme, prefs.density, prefs.decoration, prefs.fontScale, prefs.avatarBuddy]);

  const setTheme = useCallback(
    (theme: ThemeName) => setPrefs((prev) => ({ ...prev, theme })),
    [setPrefs],
  );
  const setDensity = useCallback(
    (density: Density) => setPrefs((prev) => ({ ...prev, density })),
    [setPrefs],
  );
  const setDecoration = useCallback(
    (decoration: Decoration) => setPrefs((prev) => ({ ...prev, decoration })),
    [setPrefs],
  );
  const cycleTheme = useCallback(
    () => setPrefs((prev) => ({ ...prev, theme: nextTheme(prev.theme) })),
    [setPrefs],
  );
  const setFontScale = useCallback(
    (fontScale: FontScale) => setPrefs((prev) => ({ ...prev, fontScale })),
    [setPrefs],
  );
  const setAvatarBuddy = useCallback(
    (avatarBuddy: string | undefined) => setPrefs((prev) => ({ ...prev, avatarBuddy })),
    [setPrefs],
  );
  const cycleFontScale = useCallback(
    () => setPrefs((prev) => ({ ...prev, fontScale: nextFontScale(prev.fontScale ?? FONT_SCALE_DEFAULT) })),
    [setPrefs],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme: prefs.theme,
      density: prefs.density,
      decoration: prefs.decoration,
      bg,
      fontScale: prefs.fontScale ?? FONT_SCALE_DEFAULT,
      avatarBuddy: prefs.avatarBuddy,
      setAvatarBuddy,
      setTheme,
      setDensity,
      setDecoration,
      setBg,
      cycleTheme,
      setFontScale,
      cycleFontScale,
    }),
    [prefs.theme, prefs.density, prefs.decoration, prefs.fontScale, prefs.avatarBuddy, bg, setTheme, setDensity, setDecoration, cycleTheme, setFontScale, cycleFontScale, setAvatarBuddy],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}

/**
 * Optional variant · returns null when called outside <ThemeProvider>.
 * Use this for ambient UI surfaces (e.g. ThemeToggleWidget rendered
 * inside Sidebar) that should gracefully disappear in test or partial-mount
 * contexts rather than throwing. Adopted by slot_dark_theme DT.5
 * (2026-05-18) so existing Sidebar-only test fixtures (NavShell.test.tsx)
 * keep passing without wholesale ThemeProvider wrapping.
 */
export function useThemeOptional(): ThemeContextValue | null {
  return useContext(ThemeContext);
}
